import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Container,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import InfiniteScroll from 'react-infinite-scroll-component';
import Layout from '../../components/layout/Layout';
import { useAppSelector } from '../../redux/hooks';
import { Protocol } from '../../types';
import Filter from './components/Filter';
import ProtocolCard from './components/ProtocolCard';
import ProtocolRow from './components/ProtocolRow';
import Search from './components/Search';
import { useInfiniteProtocols } from './hooks/useInfiniteProtocols';

const Protocols = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { total } = useAppSelector(state => state.protocols);
  const { protocols, loading, error, hasMore, fetchMore } = useInfiniteProtocols();

  const scrollTrigger = useScrollTrigger({
    threshold: 200,
    disableHysteresis: true,
  });

  const tableColumns = ['Título', 'Categoría', '   '];

  return (
    <Layout showSidebar={true}>
      {!isMobile ? (
        <Container
          maxWidth='lg'
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: 3,
            px: 4,
            py: 4,
            minHeight: '77vh',
          }}
        >
          {/* Título */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant='h4' fontWeight='bold' color='primary'>
              Protocolos Médicos
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              {total} protocolos disponibles
            </Typography>
          </Box>

          {/* Búsqueda y Filtros */}
          <Box display='flex' justifyContent='space-between' gap={2} mb={3} flexWrap='wrap'>
            <Search />
            <Filter />
          </Box>

          {/* Vista para escritorio (tabla) */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '80vh',
            }}
          >
            {/* Fixed Header */}
            <TableContainer
              component={Paper}
              sx={{
                backgroundColor: theme.palette.primary.main,
                borderRadius: '4px 4px 0 0',
                boxShadow: 'none',
              }}
            >
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    {tableColumns.map((header, index) => {
                      let alignment: 'left' | 'center' | 'right' = 'left';
                      if (index === 0) alignment = 'left';
                      else if (index === 1) alignment = 'center';
                      else if (index === 2) alignment = 'center';

                      return (
                        <TableCell
                          key={index}
                          align={alignment}
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            textAlign: alignment,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            width: index === 0 ? '500px' : index === 2 ? '60px' : 'auto',
                            minWidth: index === 0 ? '500px' : index === 2 ? '60px' : 'auto',
                            maxWidth: index === 0 ? '500px' : index === 2 ? '60px' : 'none',
                            paddingLeft: index === 0 ? 1 : undefined,
                          }}
                        >
                          {header}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
              </Table>
            </TableContainer>

            {/* Scrollable Content */}
            <TableContainer
              component={Paper}
              id='scrollable-table'
              sx={{
                flex: 1,
                overflowY: 'auto',
                borderRadius: '0 0 4px 4px',
                boxShadow: 'none',
                borderTop: 'none',
              }}
            >
              <InfiniteScroll
                dataLength={protocols.length}
                next={fetchMore}
                hasMore={hasMore}
                loader={
                  <Box p={10} width='100%'>
                    <Typography textAlign='center' color='text.secondary'>
                      Cargando Protocolos...
                    </Typography>
                  </Box>
                }
                scrollableTarget='scrollable-table'
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableBody>
                    {loading && protocols.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align='center' sx={{ py: 10 }}>
                          <Typography color='text.secondary'>Cargando Protocolos...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : error ? (
                      <TableRow>
                        <TableCell colSpan={3} align='center' sx={{ py: 10 }}>
                          <Typography color='error'>{error}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : protocols.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align='center' sx={{ py: 10 }}>
                          <Typography color='text.secondary'>
                            No se encontraron protocolos
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      protocols.map((protocol: Protocol) => (
                        <ProtocolRow key={protocol._id} protocol={protocol} />
                      ))
                    )}
                  </TableBody>
                </Table>
              </InfiniteScroll>
            </TableContainer>
          </Box>
        </Container>
      ) : (
        // Vista para dispositivos móviles (tarjetas)
        <Box sx={{ px: 2, py: 3 }}>
          {/* Título */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant='h4' fontWeight='bold' color='primary'>
              Protocolos Médicos
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              {total} protocolos disponibles
            </Typography>
          </Box>

          {/* Búsqueda y Filtros */}
          <Box
            display='flex'
            flexDirection='column'
            gap={2}
            mb={3}
            sx={{
              position: 'sticky',
              top: '64px',
              zIndex: 1000,
              backgroundColor: 'white',
              padding: 2,
              margin: '-16px -16px 24px -16px',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Search />
            <Filter />
          </Box>

          {/* Cards */}
          <Box sx={{ minHeight: '60vh' }}>
            {loading && protocols.length === 0 ? (
              <Box py={10} textAlign='center'>
                <Typography color='text.secondary'>Cargando Protocolos...</Typography>
              </Box>
            ) : error ? (
              <Box py={10} textAlign='center'>
                <Typography color='error'>{error}</Typography>
              </Box>
            ) : protocols.length === 0 ? (
              <Box py={10} textAlign='center'>
                <Typography color='text.secondary'>No se encontraron protocolos</Typography>
              </Box>
            ) : (
              <>
                {protocols.map((protocol: Protocol) => (
                  <ProtocolCard key={protocol._id} protocol={protocol} />
                ))}

                {/* Load More Button */}
                {hasMore && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Button
                      variant='outlined'
                      onClick={fetchMore}
                      disabled={loading}
                      sx={{
                        minWidth: 200,
                        py: 1.5,
                        px: 4,
                        borderRadius: 2,
                      }}
                    >
                      {loading ? 'Cargando...' : 'Cargar Más'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Floating button to scroll to top on mobile */}
          {scrollTrigger && (
            <Box
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 1000,
              }}
            >
              <Fab
                onClick={() =>
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  })
                }
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                <KeyboardArrowUpIcon />
              </Fab>
            </Box>
          )}
        </Box>
      )}
    </Layout>
  );
};

export default Protocols;
